import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonService } from '../../../services/common/common.service';
import { AlignDirection, CellTemplateType, FilterTemplateType, GenericColDef, GridRowSelectionType, CellActionType, GridContext, SortByFilter } from '../../../models/grid-context';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { RestService } from '../../../services/rest/rest.service';
import { DiscardPileLogAction, Endpoint, FilterAttribute, FilterOperator, HelpIndex, ModalTargetType, RemoveRestoreDocsOperationType, UpdateStatusType } from '../../../models/enums';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { HttpService } from '../../../services/http/http.service';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { isNullOrUndefined } from '../../../models/utils';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { RemoveRestoreDocsModalComponent, RemoveRestoreDocsModalData } from '../remove-restore-docs-modal/remove-restore-docs-modal.component';
import { ValueGetterParams } from 'ag-grid-community';
import { DrFilter } from '../../../models/dr-filter';

@Component({
    standalone: false,
    selector: 'res-discard-pile-log',
    templateUrl: './discard-pile-log-modal.component.html',
    styleUrls: ['./discard-pile-log-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DiscardPileLogComponent extends BaseModalComponent {
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;
    @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;

    // modal info
    modalTitle = 'Discard Pile Log';
    modalClass = 'discard-pile-log-modal';
    automationId = 'discard-pile-log';
    secondaryButtonLabel = 'Close';
    gridName = 'Discard Pile Log';
    isBusy = false;
    isBusyMessage = 'Loading Discard Pile Log ...';
    modalHelpIndex = HelpIndex.discard_pile_log_dlg;
    filters = [{ attribute: "PROJECT_ID", operator: "EQUALS", value: this.commonService.currentProject?.handle }];
    ownersList: string[];

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.projectManager_getDiscardPileLogs,
        responseName: 'tasks',
        data: {
            filters: this.filters
        },
    });

    columnDefs: Array<GenericColDef> = new Array<GenericColDef>(
        {
            headerName: 'Action',
            field: 'action',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.nameIconLabel,
            width: 35,
        },
        {
            headerName: 'Date',
            field: 'dateStarted',
            automationId: this.automationId,
            cellAlignDirection: AlignDirection.right,
            headerAlignDirection: AlignDirection.right,            
            sortBy: 'dateStarted',
            width: 50,
        },
        {
            headerName: 'Documents',
            field: 'numberResults',
            automationId: this.automationId,
            cellAlignDirection: AlignDirection.right,
            headerAlignDirection: AlignDirection.right,
            width: 40,
            minWidth: 30,
        },
        {
            headerName: 'Total Size',
            field: 'totalSize',
            automationId: this.automationId,
            cellAlignDirection: AlignDirection.right,
            headerAlignDirection: AlignDirection.right,
            width: 45,
            minWidth: 30,
        },
        {
            headerName: 'User',
            field: 'owner',
            serviceTierFilter: true,
            automationId: this.automationId,
            filterType: FilterTemplateType.comboBox,
            filterAttributeName: 'USER',
            suppressAutoSize: true,
            minWidth: 60,
            width: 90,
        },
        {
            headerName: 'Comment',
            field: 'comments',
            automationId: this.automationId,
            cellAlignDirection: AlignDirection.left,
            minWidth: 300,
        }
    );

    rowSelection = GridRowSelectionType.single;

    constructor(
        private modalService: BsModalService,
        private restService: RestService,
        private commonService: CommonService,
        private httpService: HttpService,
        private modalRef: BsModalRef,
    ) {
        super(modalService, modalRef);
         this.loadUsersList();
    }

    responseArrayCallback = (value: any) => {
        value.totalSize = value?.attributes?.find(d => d.name === 'SIZE')?.value;
        value.action = value?.description?.startsWith('Restoring ') ? DiscardPileLogAction.RESTORE : DiscardPileLogAction.DISCARD;
        value.iconClass = value.action === DiscardPileLogAction.RESTORE ? 'fa-reply color-green-05' : 'fa-recycle color-red-05';
        value.comments = value?.attributes?.find(d => d.name === 'COMMENTS')?.value;
        value.totalSize = value.totalSize ? value.totalSize + ' GB' : '';
        return value;
    }

    restoreFromDiscarPile(item) {
        this.commonService.openModal(RemoveRestoreDocsModalComponent, {
            modalData: <RemoveRestoreDocsModalData>{
                targets: [item],
                operationType: RemoveRestoreDocsOperationType.restoreFromDiscardPile,
                targetType: ModalTargetType.document,
                updateStatusType: UpdateStatusType.WORKING_SET
            }
        });
    }

    loadUsersList(): void {
        this.restService.post(Endpoint.projectManager_listWorkbasketUsers, { handle: this.commonService.currentProject.handle }).then(response => {
            if (response && response.entityNames) {
                const colDef = this.columnDefs.find(f => f.field === 'owner');
                if (colDef) {
                    this.ownersList = response.entityNames ? response.entityNames.filter(e => e !== null) : response.entityNames;
                    this.ownersList.sort();
                    colDef.filterItems = this.ownersList;
                    this.genericGrid.setColumnDefs(this.columnDefs);
                    this.genericGrid.refreshColumns();
                }
            }
        });
    }


    gridContext = new GridContext({
        inlineGridFilter: true,
        sortAscending: false,
        sortAscendingFieldName: 'descending',
        sortBy: 'dateStarted',
        onFilterApply: (queryStr: string, filterType: any, headerName: any) => this.onFilterApply(queryStr, filterType, headerName),
    });

    downloadGrid(node: GenericTreeNode): void {
        this.restService.post(Endpoint.projectManager_getDiscardPileLogDownloadUrl, {
            filters: this.filters,
            descending: this.requestInfo.data.descending
        }).then(response => {
            if (response) {
                if (response?.fileUrl) {
                    this.commonService.unzipFile(response.fileUrl, this.downloadLink.nativeElement, null);
                }
            }
        });
    }
    openHelp = (): void => this.commonService.openHelp(this.modalHelpIndex);

    onFilterApply(queryStr: string, filterType: any, headerName: any): void {
        const filterIndex = this.filters.findIndex(f => f.attribute === FilterAttribute.OWNER);
        if (queryStr && queryStr.trim() !== '') {
            if (filterIndex > -1) {
                this.filters[filterIndex].value = queryStr;
            } else {
                this.filters.push({
                    attribute: FilterAttribute.OWNER,
                    operator: FilterOperator.LIKE,
                    value: queryStr
                });
            }
        }
        if (filterType === FilterTemplateType.clear) {
            this.filters.splice(filterIndex, 1);
        }
        this.requestInfo.data.filters = this.filters;
        this.genericGrid.loadData();
    }
}
